import { CustomerContext } from '@typesApp/Customer'
import { AuthToken, AuthTokenArgs, Customer } from '@typesApp/Customer'
import { buildAuthTokenArgs } from '@utils/authToken'
import { CustomerChangedError, InvalidCustomerError } from '@utils/errorUtil'
import { formatCommerceCode } from '@utils/formatCommerceCode'
import { isCustomerNumberChanged, isCustomerNumberValid } from '@utils/isCustomerNumberValid'
import { sanitizeCustomer } from '@utils/sanitizeCustomer'

export type ReauthService = {
  fetchCustomer: () => Promise<Customer>
  fetchAuthToken: (args: AuthTokenArgs) => Promise<AuthToken>
}

type FetchStatusType = number | string | undefined

export type ReauthControllerParams = {
  ecommerceCode: string
  ecommerceTransactionHost: string | undefined
  currentCustomerContext: CustomerContext | undefined
  currentCustomer: Customer | undefined
  anonymousUserId: string | undefined
}

export type ReauthController = {
  /**
   * Reauthorize by re-fetching auth token
   * @throws InvalidCustomerError if customer is no longer valid
   * @throws CustomerChangedError if customer has changed
   * @throws Error on all other errors
   */
  reauthOrThrow: () => Promise<void>
}

export const reauthController = (service: ReauthService, params: ReauthControllerParams): ReauthController => {
  const { ecommerceCode, ecommerceTransactionHost, currentCustomerContext, currentCustomer, anonymousUserId } =
    params ?? {}

  const reauthOrThrow = async (): Promise<void> => {
    const customer = await validateCustomerOrThrow()

    // NOTE: successful fetch triggers authToken redux state to update within
    // connectorSlice so that subsequent requests have immediate access to authToken
    await refetchToken(customer)
  }

  const validateCustomerOrThrow = async (): Promise<Customer> => {
    let customer: Customer | undefined

    try {
      customer = await service.fetchCustomer()
    } catch (error) {
      // ecommerce may throw error if there is no valid customer
      throw new InvalidCustomerError(`Got invalid customer from commerce - ${error}`)
    }

    const sanitizedCustomer = sanitizeCustomer(customer)
    if (!isCustomerNumberValid(sanitizedCustomer?.id, anonymousUserId)) {
      throw new InvalidCustomerError(`Invalid customer number ${sanitizedCustomer?.id ?? 'unknown'}`)
    }

    if (isCustomerNumberChanged(currentCustomer?.id, sanitizedCustomer?.id)) {
      throw new CustomerChangedError(`Customer number ${currentCustomer?.id} changed to ${sanitizedCustomer?.id}`)
    }

    return sanitizedCustomer
  }

  const refetchToken = async (customer: Customer): Promise<AuthToken> | never => {
    const ecommerce = formatCommerceCode({
      ecommerceCode,
      ecommerceTransactionHost,
    })

    const args = buildAuthTokenArgs(ecommerce, customer, currentCustomerContext)

    return await service.fetchAuthToken(args)
  }

  return {
    reauthOrThrow,
  }
}

export const isRequireReauth = (
  status: FetchStatusType,
  isSessionExpired?: boolean,
  isLoggingOut?: boolean
): boolean => {
  if (status !== 401) return false

  return !isSessionExpired && !isLoggingOut
}
